import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '../stores/auth-store';

// Lazy-loaded pages
import { lazy, Suspense } from 'react';

const LoginPage = lazy(() => import('../features/auth/LoginPage'));
const RegisterPage = lazy(() => import('../features/auth/RegisterPage'));
const DashboardPage = lazy(() => import('../features/dashboard/DashboardPage'));
const WizardPage = lazy(() => import('../features/wizard/WizardPage'));
const AIStudioPage = lazy(() => import('../features/ai-studio/AIStudioPage'));
const VenuePage = lazy(() => import('../features/venue/VenuePage'));
const BudgetPage = lazy(() => import('../features/budget/BudgetPage'));
const TasksPage = lazy(() => import('../features/tasks/TasksPage'));
const MessagesPage = lazy(() => import('../features/messages/MessagesPage'));
const SettingsPage = lazy(() => import('../features/settings/SettingsPage'));
const GuestPage = lazy(() => import('../features/guest/GuestPage'));
const GalleryPage = lazy(() => import('../features/gallery/GalleryPage'));
const EventDayPage = lazy(() => import('../features/runbook/EventDayPage'));
const VendorPage = lazy(() => import('../features/vendor/VendorPage'));
const AgentDashboardPage = lazy(() => import('../features/agent/AgentDashboardPage'));
const AgentSettingsPage = lazy(() => import('../features/agent/AgentSettingsPage'));

function AuthLoader() {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      gap: '12px',
      color: 'var(--text-secondary)',
    }}>
      <div style={{
        width: '36px',
        height: '36px',
        border: '3px solid var(--border)',
        borderTopColor: 'var(--color-primary)',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isLoading = useAuthStore((s) => s.isLoading);
  if (isLoading) return <AuthLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isLoading = useAuthStore((s) => s.isLoading);
  if (isLoading) return <AuthLoader />;
  if (isAuthenticated) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function PageLoader() {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      color: 'var(--text-secondary)',
    }}>
      Loading...
    </div>
  );
}

export function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />

        {/* Protected routes */}
        <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/wizard/:eventId?" element={<ProtectedRoute><WizardPage /></ProtectedRoute>} />
        <Route path="/event/:eventId" element={<ProtectedRoute><AIStudioPage /></ProtectedRoute>} />
        <Route path="/event/:eventId/venue" element={<ProtectedRoute><VenuePage /></ProtectedRoute>} />
        <Route path="/event/:eventId/budget" element={<ProtectedRoute><BudgetPage /></ProtectedRoute>} />
        <Route path="/event/:eventId/tasks" element={<ProtectedRoute><TasksPage /></ProtectedRoute>} />
        <Route path="/event/:eventId/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
        <Route path="/event/:eventId/guests" element={<ProtectedRoute><GuestPage /></ProtectedRoute>} />
        <Route path="/event/:eventId/gallery" element={<ProtectedRoute><GalleryPage /></ProtectedRoute>} />
        <Route path="/event/:eventId/runbook" element={<ProtectedRoute><EventDayPage /></ProtectedRoute>} />
        <Route path="/event/:eventId/vendors" element={<ProtectedRoute><VendorPage /></ProtectedRoute>} />
        <Route path="/event/:eventId/agent" element={<ProtectedRoute><AgentDashboardPage /></ProtectedRoute>} />
        <Route path="/event/:eventId/agent/settings" element={<ProtectedRoute><AgentSettingsPage /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
